import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";

/** The kit as stored (and as a Build with AI draft carries it). */
export interface BrandKitValue {
  palette?: { primary?: string; secondary?: string; background?: string; text?: string; accents?: string[] };
  fonts?: { heading?: string; body?: string };
  tone?: string;
}

/** The palette as swatches, plus (with `withType`) the fonts and tone in words. */
export function BrandKitPreview({ kit, withType = false }: Readonly<{ kit: BrandKitValue; withType?: boolean }>) {
  const { t } = useTranslation("brands", { keyPrefix: "kit" });
  const palette = kit.palette ?? {};
  const swatches = [
    { label: t("primary"), value: palette.primary },
    { label: t("secondary"), value: palette.secondary },
    { label: t("background"), value: palette.background },
    { label: t("text"), value: palette.text },
    ...(palette.accents ?? []).map((value) => ({ label: t("accents"), value })),
  ].filter((s): s is { label: string; value: string } => Boolean(s.value));
  const type = withType
    ? [
        kit.fonts?.heading && `${t("headingFont")}: ${kit.fonts.heading}`,
        kit.fonts?.body && `${t("bodyFont")}: ${kit.fonts.body}`,
        kit.tone && `${t("tone")}: ${kit.tone}`,
      ].filter((line): line is string => Boolean(line))
    : [];
  return (
    <div className="space-y-3">
      {swatches.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {swatches.map((s) => (
            <Swatch key={`${s.label}-${s.value}`} label={s.label} value={s.value} />
          ))}
        </div>
      )}
      {type.map((line) => (
        <Text key={line} variant="body-sm" tone="secondary">
          {line}
        </Text>
      ))}
    </div>
  );
}

function Swatch({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        aria-hidden
        className="size-10 rounded-lg border border-border-subtle"
        // conventions-ignore: no-raw-colors brand swatches render the user's stored hex.
        style={{ backgroundColor: value }}
      />
      <Text variant="caption" tone="tertiary">
        {label}
      </Text>
      <Text variant="caption" tone="secondary">
        {value}
      </Text>
    </div>
  );
}
